/**
 * Notification Delivery Status Card
 * 
 * Admin component showing delivery queue statistics and recent failures.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  RefreshCw,
  Mail,
  Loader2,
} from 'lucide-react';
import { useDeliveryStats, useRecentFailures, useRetryFailedDelivery } from '@/hooks/notifications';
import { useMyProfile } from '@/hooks/profile';
import { formatDistanceToNow } from 'date-fns';

export function NotificationDeliveryCard() {
  const { data: profile } = useMyProfile();
  const companyId = profile?.company_id;
  
  const { data: stats, isLoading: statsLoading, isFetching: statsFetching, refetch: refetchStats } = useDeliveryStats(companyId);
  const { data: failures, isLoading: failuresLoading, isFetching: failuresFetching, refetch: refetchFailures } = useRecentFailures(companyId, 5);
  const { mutate: retryDelivery, isPending: isRetrying } = useRetryFailedDelivery(companyId);
  
  const isLoading = statsLoading || failuresLoading;
  const isRefreshing = statsFetching || failuresFetching;
  
  const handleRefresh = () => {
    refetchStats();
    refetchFailures();
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Delivery Status</CardTitle>
            <CardDescription>Current queue + recent activity (delivered: 24h, failed: 7d)</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg border bg-card text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Pending</span>
            </div>
            <div className="text-2xl font-bold">{stats?.pending ?? 0}</div>
          </div>
          
          <div className="p-4 rounded-lg border bg-card text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
              <span className="text-sm font-medium">Processing</span>
            </div>
            <div className="text-2xl font-bold">{stats?.processing ?? 0}</div>
          </div>
          
          <div className="p-4 rounded-lg border bg-card text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Delivered (24h)</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{stats?.delivered ?? 0}</div>
          </div>
          
          <div className="p-4 rounded-lg border bg-card text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium">Failed (7d)</span>
            </div>
            <div className="text-2xl font-bold text-destructive">{stats?.failed ?? 0}</div>
          </div>
        </div>
        
        {/* Recent failures */}
        {failures && failures.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Recent Failures</h4>
            <div className="space-y-2">
              {failures.map(failure => (
                <div
                  key={failure.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-destructive/5"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">
                        {failure.recipient_email || 'Unknown recipient'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {failure.error_details?.error as string || 'Unknown error'} • 
                        {failure.attempts} attempts • 
                        {formatDistanceToNow(new Date(failure.updated_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => retryDelivery(failure.id)}
                    disabled={isRetrying}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* No failures message */}
        {failures && failures.length === 0 && stats?.failed === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm">No delivery failures</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
